import type {
  CronTaskDefinitionDTO,
  CronTasksAdminApiClient,
  SchedulePreset,
} from '@modules/content-plan/services/CronTasksAdminApiClient';

export const META_ADS_METADATA_SYNC_TASK_TYPE = 'meta-ads-metadata-sync';
export const META_ADS_INSIGHTS_SYNC_TASK_TYPE = 'meta-ads-insights-sync';
export const META_ADS_LIFETIME_SYNC_TASK_TYPE = 'meta-ads-lifetime-sync';

export interface MetaAdsCronScheduleInput {
  projectId: string;
  userId: string;
  adAccountId: string;
  adAccountLabel?: string;
  namePrefix?: string;
}

export interface MetaAdsCronScheduleResult {
  task: CronTaskDefinitionDTO;
  created: boolean;
}

type CronTaskClient = Pick<CronTasksAdminApiClient, 'listDefinitions' | 'createTask' | 'updateTask'>;

export class MetaAdsCronScheduleService {
  constructor(private readonly client: CronTaskClient) {}

  async upsertMetadataSync(input: MetaAdsCronScheduleInput): Promise<MetaAdsCronScheduleResult> {
    return this.upsert({
      input,
      taskType: META_ADS_METADATA_SYNC_TASK_TYPE,
      preset: '@daily',
      name: `Meta Ads metadata sync: ${input.adAccountLabel || input.adAccountId}`,
      description: 'Daily Meta Ads campaign/adset/ad/creative metadata sync for paid acquisition reporting.',
      payload: stripUndefined({
        projectId: input.projectId,
        adAccountId: input.adAccountId,
        // Page 50 avoids Meta transient 500s on heavy /ads listings; 20 pages
        // keeps the 1000-objects-per-type coverage (Package B4).
        limit: 50,
        maxPages: 20,
        namePrefix: input.namePrefix?.trim() || undefined,
      }),
    });
  }

  async upsertInsightsSync(input: MetaAdsCronScheduleInput): Promise<MetaAdsCronScheduleResult> {
    return this.upsert({
      input,
      taskType: META_ADS_INSIGHTS_SYNC_TASK_TYPE,
      preset: '@hourly',
      name: `Meta Ads insights sync: ${input.adAccountLabel || input.adAccountId}`,
      description: 'Hourly Meta Ads delivery/action sync for the last three calendar days.',
      payload: {
        projectId: input.projectId,
        adAccountId: input.adAccountId,
        lookbackDays: 3,
        sliceDays: 1,
        limit: 100,
      },
    });
  }

  /**
   * Fatigue R1.2/R1.3 — daily cumulative-window (lifetime reach/frequency)
   * pull + current-state ad diagnostics sampling. The diagnostics feed is
   * non-reconstructible on Meta's side, so this rides the same scheduling
   * click as the insights sync instead of adding a separate UI surface.
   */
  async upsertLifetimeSync(input: MetaAdsCronScheduleInput): Promise<MetaAdsCronScheduleResult> {
    return this.upsert({
      input,
      taskType: META_ADS_LIFETIME_SYNC_TASK_TYPE,
      preset: '@daily',
      name: `Meta Ads lifetime sync: ${input.adAccountLabel || input.adAccountId}`,
      description: 'Daily cumulative-window lifetime observation + ad diagnostics sampling for creative fatigue analytics.',
      payload: {
        projectId: input.projectId,
        adAccountId: input.adAccountId,
        limit: 500,
      },
    });
  }

  private async upsert(args: {
    input: MetaAdsCronScheduleInput;
    taskType: string;
    preset: SchedulePreset;
    name: string;
    description: string;
    payload: Record<string, unknown>;
  }): Promise<MetaAdsCronScheduleResult> {
    const existing = await this.findExisting(args.input.projectId, args.taskType, args.input.adAccountId);
    if (existing) {
      const response = await this.client.updateTask(existing.id, {
        name: args.name,
        description: args.description,
        status: 'active',
        schedule: { preset: args.preset },
        payload: args.payload,
      });
      if (!response.success || !response.task) {
        throw new Error(response.error || `Failed to update ${args.taskType}`);
      }
      return { task: response.task, created: false };
    }

    const response = await this.client.createTask({
      taskType: args.taskType,
      projectId: args.input.projectId,
      name: args.name,
      description: args.description,
      schedule: { preset: args.preset },
      status: 'active',
      payload: args.payload,
      userId: args.input.userId,
    });
    if (!response.success || !response.task) {
      throw new Error(response.error || `Failed to create ${args.taskType}`);
    }
    return { task: response.task, created: true };
  }

  private async findExisting(
    projectId: string,
    taskType: string,
    adAccountId: string,
  ): Promise<CronTaskDefinitionDTO | null> {
    const response = await this.client.listDefinitions({ projectId, taskType });
    if (!response.success) {
      throw new Error(response.error || `Failed to list ${taskType} definitions`);
    }

    return (response.definitions ?? []).find((definition) => {
      if (definition.projectId !== projectId) return false;
      if (definition.taskType !== taskType) return false;
      return definition.payload?.adAccountId === adAccountId;
    }) ?? null;
  }
}

function stripUndefined<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined),
  ) as T;
}
